import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  type FacilityTransferDirectionPublicRecord,
  type FacilityTransferItemPublicRecord,
  WheelExternalCheckStatus
} from '@navio/nsdk';
import { NoddiAsyncConfirmationDialog, NoddiBasicCard, NoddiButton, NoddiSearchBar, NoddiTabs } from 'noddi-ui-web';
import { useMemo, useState } from 'react';
import { getTabsTranslations } from '../../utils/commonTranslations';
import { EmptyDay } from './EmptyDay';
import { OrderNotes } from './OrderNotes';
import { PickListPrint } from './PickListPrint';
import { PickupIntro } from './PickupIntro';
import { PickupOrderBanner } from './PickupOrderBanner';
import { compareByRoundThenOwnerThenPlate } from './pickupRounds';
import { ReadyProgress } from './ReadyProgress';
import { RecentChanges } from './RecentChanges';
import { useSaveExternalChecks } from './useSaveExternalChecks';
import { type StatusFilter, WheelSetList } from './WheelSetList';

interface Props {
  outgoing: FacilityTransferDirectionPublicRecord;
  facilityName: string;
  pickupDay: string;
}

export const OutgoingTab = ({ outgoing, facilityName, pickupDay }: Props) => {
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');

  const {
    mutateAsync: saveChecks,
    isPending: isMarkingAllReady,
    error: markAllError,
    publicSlug
  } = useSaveExternalChecks();

  const sortedItems = useMemo<FacilityTransferItemPublicRecord[]>(
    () => [...outgoing.items].sort(compareByRoundThenOwnerThenPlate),
    [outgoing.items]
  );

  const searchedItems = useMemo<FacilityTransferItemPublicRecord[]>(() => {
    const needle = searchText.trim().toLowerCase();
    return sortedItems.filter((item) => {
      if (needle === '') {
        return true;
      }
      const haystack = [item.licensePlate, item.ownerName, item.carMake, item.carModel, item.orderSlug]
        .filter((v): v is string => typeof v === 'string')
        .join(' ')
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [sortedItems, searchText]);

  if (outgoing.items.length === 0) {
    return <EmptyDay />;
  }

  const itemsWithStatus = (status: StatusFilter) =>
    status === 'ALL' ? searchedItems : searchedItems.filter((item) => item.externalCheckStatus === status);
  const visibleItems = itemsWithStatus(statusFilter);

  // Reported issues and completed orders are left alone: only unanswered, editable wheel sets are bulk-marked.
  const visiblePendingItems = visibleItems.filter(
    (item) => item.isEditable && item.externalCheckStatus === WheelExternalCheckStatus.PENDING
  );
  const visiblePendingCount = visiblePendingItems.length;
  const hasEditableItems = outgoing.items.some((item) => item.isEditable);

  const markAllVisibleReady = async () => {
    await saveChecks({
      publicSlug,
      items: visiblePendingItems.map((item) => ({ itemId: item.id, status: WheelExternalCheckStatus.READY }))
    });
  };

  const totalCount = outgoing.items.length;
  const readyCount = outgoing.items.filter(
    (item) => item.externalCheckStatus === WheelExternalCheckStatus.READY
  ).length;
  const issueCount = outgoing.items.filter(
    (item) => item.externalCheckStatus === WheelExternalCheckStatus.ISSUE
  ).length;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-col gap-2'>
        <PickupIntro
          facilityName={facilityName}
          pickupDay={pickupDay}
          itemCount={totalCount}
          rounds={outgoing.rounds}
        />
        <RecentChanges changes={outgoing.recentChanges} />
      </div>

      <PickupOrderBanner pickupRounds={outgoing.rounds} />
      <OrderNotes notes={outgoing.notes} />
      <ReadyProgress readyCount={readyCount} issueCount={issueCount} totalCount={totalCount} />

      {!hasEditableItems && (
        <NoddiBasicCard className='border-warning bg-warning/5'>
          <p>
            <Trans>These transfer orders are completed, so answers can no longer be changed.</Trans>
          </p>
        </NoddiBasicCard>
      )}

      <div className='flex flex-col gap-2 sm:flex-row sm:items-end'>
        <div className='flex-1'>
          <NoddiSearchBar placeholder={t`Search`} handleChange={(e) => setSearchText(e.target.value)} />
        </div>
        <PickListPrint facilityName={facilityName} pickupDay={pickupDay} items={sortedItems} rounds={outgoing.rounds} />
        {hasEditableItems && (
          <NoddiAsyncConfirmationDialog
            title={t`Mark ${visiblePendingCount} wheel sets as ready?`}
            description={t`Only do this when every wheel set in the list is ready to be picked up.`}
            cancelText={t`Cancel`}
            confirmText={t`Mark as ready`}
            confirmAction={markAllVisibleReady}
            confirmActionLoading={isMarkingAllReady}
            trigger={
              <NoddiButton variant='outline' disabled={visiblePendingCount === 0}>
                <Trans>Mark all as ready ({visiblePendingCount})</Trans>
              </NoddiButton>
            }
          >
            {markAllError && <ApiErrorMessageWithTrans error={markAllError} />}
          </NoddiAsyncConfirmationDialog>
        )}
      </div>

      {/* Status tabs stay local state: the direction tabs above own the `tab` query param. */}
      <NoddiTabs<StatusFilter>
        ariaLabel={t`Status`}
        translations={getTabsTranslations()}
        defaultValue='ALL'
        value={statusFilter}
        onValueChange={setStatusFilter}
        tabItems={STATUS_TABS.map(({ value, label }) => {
          const tabItems = itemsWithStatus(value);
          return {
            value,
            label: label(),
            count: tabItems.length,
            // Empty status tabs would only push the rest into the overflow menu on phones.
            hide: value !== 'ALL' && value !== statusFilter && tabItems.length === 0,
            node: (
              <WheelSetList
                items={tabItems}
                rounds={outgoing.rounds}
                statusFilter={value}
                isSearching={searchText.trim() !== ''}
              />
            )
          };
        })}
      />
    </div>
  );
};

const STATUS_TABS: { value: StatusFilter; label: () => string }[] = [
  { value: 'ALL', label: () => t`All` },
  { value: WheelExternalCheckStatus.PENDING, label: () => t`Unanswered` },
  { value: WheelExternalCheckStatus.ISSUE, label: () => t`Issue` },
  { value: WheelExternalCheckStatus.READY, label: () => t`Marked ready` }
];
